import {
  TAuthStep,
  TSrpLoginParams,
  TSrpLoginResponse,
  srpLogin as frankenSrpLogin,
} from "franken-srp";

import { localUserWithTokensSchema } from "@smart/bridge-local-schemas-basic";
import { wrapSchema } from "@smart/itops-utils-basic";

import { isLocal, localFetch } from "./local";

export type CognitoLoginParams = Omit<
  TSrpLoginParams,
  "username" | "password" | "device"
>;

async function* localLogin({
  username,
  password,
  device,
}: TSrpLoginParams): TSrpLoginResponse {
  try {
    const user = await localFetch(
      "login",
      {
        username,
        password,
        accessExpiresInMin: 60,
        refreshExpiresInMin: 60 * 24 * 7,
        device: device?.key,
      },
      wrapSchema(localUserWithTokensSchema),
      "srp-local",
    );

    if (user.mfa.type !== "Off") {
      const step: TAuthStep =
        user.mfa.type === "Software"
          ? { code: "SOFTWARE_MFA_REQUIRED" }
          : { code: "SMS_MFA_REQUIRED", hint: "+61******789" };

      let input;
      let errorResponse;
      do {
        input = yield { ...step, error: errorResponse };
        const inputMatch: boolean = !!input.match(/^[0-9]+$/);
        errorResponse = new Error(
          input && inputMatch
            ? `Incorrect MFA Code`
            : `Expected 6 digit MFA code, received: ${input}`,
        );
      } while (input !== user.mfa.code);
    }

    return {
      code: "TOKENS",
      response: {
        username: user.username,
        tokens: user.tokens,
        newDevice: device?.password
          ? {
              key: device.key,
              groupKey: device.groupKey,
              password: device.password,
              deviceAutoConfirmed: true,
              userConfirmationNecessary: false,
            }
          : undefined,
      },
    };
  } catch (err) {
    return {
      code: "ERROR",
      error: err as Error,
    };
  }
}

export const login: typeof frankenSrpLogin = isLocal
  ? localLogin
  : frankenSrpLogin;
